// src/Views/Profile/Profile.js
// UPDATED: 
// - Fixed status display to use AuthContext properly
// - Added Billing section (Web only) with Whop trial cancellation
// - Delete Account feature (Required by Apple App Store)

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import './Profile.css';
import { useAuth } from '../../context/AuthContext';
import { getAuth, sendPasswordResetEmail, deleteUser, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { doc, deleteDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { Capacitor } from '@capacitor/core';
import { Purchases } from '@revenuecat/purchases-capacitor';
import { getFunctions, httpsCallable } from 'firebase/functions';

const Profile = () => {
    const navigate = useNavigate();
    
    // 🔥 FIXED: Use AuthContext directly for accurate status
    const { 
        user, 
        signOutUser,
        isPremium,
        isPaidPremium,
        isTrialActive,
        trialDaysLeft,
        hasUsedTrial,
        profile
    } = useAuth();

    const [msg, setMsg] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    // Delete Account State
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteStep, setDeleteStep] = useState(1);
    const [deletePassword, setDeletePassword] = useState('');
    const [deleteError, setDeleteError] = useState('');
    const [deleting, setDeleting] = useState(false);

    // 🔥 NEW: Billing/Cancel Trial State
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [cancelling, setCancelling] = useState(false);
    const [cancelError, setCancelError] = useState('');

    // Platform detection
    const isIOS = Capacitor.getPlatform() === 'ios';
    const isNativePlatform = Capacitor.isNativePlatform();

    const handleLogout = async () => {
        try {
            await signOutUser();
            navigate('/signin');
        } catch (err) {
            console.error('Logout failed', err);
            setError('Failed to terminate session.');
        }
    };

    const handleResetPassword = async () => {
        if (!user?.email) return;
        setLoading(true);
        setMsg('');
        setError('');
        try {
            const auth = getAuth();
            await sendPasswordResetEmail(auth, user.email);
            setMsg('Password reset link sent to your email.');
        } catch (err) {
            console.error(err);
            setError('Could not send reset email. Try again later.');
        } finally {
            setLoading(false);
        }
    };

    const copyToClipboard = (text) => {
        if (!text) return;
        navigator.clipboard.writeText(text);
        setMsg('User ID copied to clipboard.');
        setTimeout(() => setMsg(''), 2000);
    };

    // =========================================================
    // 🔥 CANCEL WHOP TRIAL/SUBSCRIPTION
    // =========================================================

    const handleCancelTrial = async () => {
        setCancelling(true);
        setCancelError('');

        try {
            const functions = getFunctions();
            const cancelWhopSubscription = httpsCallable(functions, 'cancelWhopSubscription');
            
            const result = await cancelWhopSubscription();
            
            if (result.data.success) {
                setShowCancelModal(false);
                setMsg('Your subscription has been cancelled. You will retain access until the end of your current period.');
                setTimeout(() => setMsg(''), 5000);
            } else {
                setCancelError(result.data.message || 'Failed to cancel subscription.');
            }
        } catch (err) {
            console.error('Cancel error:', err);
            setCancelError(err.message || 'Could not cancel subscription. Please contact support.');
        } finally {
            setCancelling(false);
        }
    };

    // =========================================================
    // DELETE ACCOUNT FUNCTIONS
    // =========================================================

    const handleOpenDeleteModal = () => {
        setShowDeleteModal(true);
        setDeleteStep(1);
        setDeletePassword('');
        setDeleteError('');
    };

    const handleCloseDeleteModal = () => {
        setShowDeleteModal(false);
        setDeleteStep(1);
        setDeletePassword('');
        setDeleteError('');
    };

    const handleConfirmDelete = () => {
        setDeleteStep(2);
        setDeleteError('');
    };

    const handleDeleteAccount = async () => {
        if (!user) return;

        setDeleting(true);
        setDeleteError('');

        try {
            const auth = getAuth();
            const currentUser = auth.currentUser;

            if (deletePassword) {
                try {
                    const credential = EmailAuthProvider.credential(user.email, deletePassword);
                    await reauthenticateWithCredential(currentUser, credential);
                } catch (authErr) {
                    console.error('Re-auth failed:', authErr);
                    if (authErr.code === 'auth/wrong-password' || authErr.code === 'auth/invalid-credential') {
                        setDeleteError('Incorrect password. Please try again.');
                    } else if (authErr.code === 'auth/too-many-requests') {
                        setDeleteError('Too many attempts. Please wait a few minutes.');
                    } else {
                        setDeleteError('Authentication failed. Please try again.');
                    }
                    setDeleting(false);
                    return;
                }
            }

            setDeleteStep(3);

            if (Capacitor.getPlatform() === 'ios') {
                try {
                    await Purchases.logOut();
                } catch (rcErr) {
                    console.log('[Profile] RevenueCat logout error:', rcErr);
                }
            }

            try {
                const userDocRef = doc(db, 'users', user.uid);
                await deleteDoc(userDocRef);
            } catch (fsErr) {
                console.log('[Profile] Firestore delete error:', fsErr);
            }

            await deleteUser(currentUser);

            navigate('/signin', { 
                state: { message: 'Your account has been permanently deleted.' } 
            });

        } catch (err) {
            console.error('Delete account error:', err);
            
            if (err.code === 'auth/requires-recent-login') {
                setDeleteError('Session expired. Please enter your password to continue.');
                setDeleteStep(2);
            } else {
                setDeleteError('Could not delete account. Please try again or contact support.');
            }
            setDeleting(false);
        }
    };

    // =========================================================
    // 🔥 FIXED: Status Logic using AuthContext
    // =========================================================
    
    const getStatusInfo = () => {
        if (isPaidPremium) {
            return { color: 'gold', label: 'Premium Active', icon: 'solar:crown-star-bold' };
        }
        if (isTrialActive) {
            return { color: 'green', label: 'Trial Active', icon: 'solar:clock-circle-bold' };
        }
        if (hasUsedTrial) {
            return { color: 'red', label: 'Trial Expired', icon: 'solar:alarm-bold' };
        }
        return { color: 'gray', label: 'Free Account', icon: 'solar:user-linear' };
    };

    const statusInfo = getStatusInfo();

    // Get subscription end date if available
    const getSubscriptionEndDate = () => {
        if (profile?.subscriptionEnd) {
            const date = profile.subscriptionEnd.toDate ? profile.subscriptionEnd.toDate() : new Date(profile.subscriptionEnd);
            return date.toLocaleDateString([], { month: 'long', day: 'numeric', year: 'numeric' });
        }
        if (profile?.premiumUntil) {
            const date = profile.premiumUntil.toDate ? profile.premiumUntil.toDate() : new Date(profile.premiumUntil);
            return date.toLocaleDateString([], { month: 'long', day: 'numeric', year: 'numeric' });
        }
        if (profile?.trialEnd && isTrialActive) {
            const date = profile.trialEnd.toDate ? profile.trialEnd.toDate() : new Date(profile.trialEnd);
            return date.toLocaleDateString([], { month: 'long', day: 'numeric', year: 'numeric' });
        }
        return null;
    };

    const subscriptionEndDate = getSubscriptionEndDate();

    // Get plan type
    const getPlanType = () => {
        if (profile?.premiumPlan) {
            return profile.premiumPlan.charAt(0).toUpperCase() + profile.premiumPlan.slice(1);
        }
        if (profile?.plan === 'premium') return 'Premium';
        if (isTrialActive) return 'Trial';
        return 'Free';
    };

    return (
        <section className="profile">
            <div className="profile__inner">
                
                {/* --- HEADER --- */}
                <header className="profile__header">
                    <div className="profile__eyebrow">
                        <Icon icon="solar:shield-user-linear" className="profile__eyebrow-icon" />
                        <span>Identity Hub</span>
                    </div>
                    <h1 className="profile__title">Command Identity</h1>
                    <p className="profile__subtitle">
                        Manage your security settings, subscription parameters, and account data.
                    </p>
                </header>

                {/* --- NOTIFICATIONS --- */}
                {msg && (
                    <div className="profile__alert profile__alert--success">
                        <Icon icon="solar:check-circle-linear" /> {msg}
                    </div>
                )}
                {error && (
                    <div className="profile__alert profile__alert--error">
                        <Icon icon="solar:danger-triangle-linear" /> {error}
                    </div>
                )}

                <div className="profile__grid">

                    {/* --- LEFT COL: SUBSCRIPTION STATUS --- */}
                    <div className="profile__col-left">
                        <div className={`profile__card profile__card--${statusInfo.color}`}>
                            <div className="profile__card-head">
                                <div className="profile__card-icon">
                                    <Icon icon={statusInfo.icon} />
                                </div>
                                <div>
                                    <h3 className="profile__card-title">Subscription Status</h3>
                                    <p className="profile__card-subtitle">Current clearance level</p>
                                </div>
                            </div>

                            <div className="profile__status-row">
                                <span className={`profile__badge profile__badge--${statusInfo.color}`}>
                                    {statusInfo.label}
                                </span>
                                {isTrialActive && trialDaysLeft > 0 && (
                                    <span className="profile__meta-text">
                                        {trialDaysLeft} day{trialDaysLeft !== 1 ? 's' : ''} remaining
                                    </span>
                                )}
                            </div>

                            {/* Plan Details */}
                            {(isPaidPremium || isTrialActive) && (
                                <div style={{ 
                                    marginTop: '12px', 
                                    padding: '12px', 
                                    background: 'rgba(0,0,0,0.2)', 
                                    borderRadius: '8px',
                                    fontSize: '0.85rem'
                                }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                                        <span style={{ color: '#94a3b8' }}>Plan</span>
                                        <span style={{ color: '#e2e8f0' }}>{getPlanType()}</span>
                                    </div>
                                    {subscriptionEndDate && (
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <span style={{ color: '#94a3b8' }}>{isTrialActive ? 'Trial Ends' : 'Renews'}</span>
                                            <span style={{ color: '#e2e8f0' }}>{subscriptionEndDate}</span>
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="profile__divider" />

                            <div className="profile__actions">
                                {!isPaidPremium && (
                                    <button 
                                        className="profile__btn profile__btn--primary"
                                        onClick={() => navigate('/pricing')}
                                    >
                                        {isTrialActive ? 'Upgrade to Premium' : 'Get Premium'}
                                    </button>
                                )}
                                <button 
                                    className="profile__btn profile__btn--ghost"
                                    onClick={() => navigate('/pricing')}
                                >
                                    View Plans
                                </button>
                            </div>
                        </div>

                        {/* =========================================================
                            🔥 NEW: BILLING SECTION (Web Only)
                        ========================================================= */}
                        {!isIOS && !isNativePlatform && (isPaidPremium || isTrialActive) && (
                            <div className="profile__card" style={{ marginTop: '16px' }}>
                                <div className="profile__card-head">
                                    <div className="profile__card-icon" style={{ background: 'rgba(251, 191, 36, 0.15)', color: '#fbbf24' }}>
                                        <Icon icon="solar:card-bold" />
                                    </div>
                                    <div>
                                        <h3 className="profile__card-title">Billing</h3>
                                        <p className="profile__card-subtitle">Manage your subscription</p>
                                    </div>
                                </div>

                                <div style={{ 
                                    marginTop: '16px',
                                    padding: '14px',
                                    background: 'rgba(30, 41, 59, 0.5)',
                                    borderRadius: '10px',
                                    border: '1px solid rgba(71, 85, 105, 0.3)'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                                        <Icon icon="solar:wallet-money-bold" style={{ color: '#60a5fa', fontSize: '1.2rem' }} />
                                        <div>
                                            <p style={{ margin: 0, color: '#e2e8f0', fontWeight: '500' }}>
                                                {isTrialActive ? 'Free Trial' : `${getPlanType()} Plan`}
                                            </p>
                                            <p style={{ margin: 0, color: '#64748b', fontSize: '0.8rem' }}>
                                                via Whop
                                            </p>
                                        </div>
                                    </div>

                                    {/* Cancel Button */}
                                    <button
                                        onClick={() => setShowCancelModal(true)}
                                        style={{
                                            width: '100%',
                                            padding: '10px 16px',
                                            borderRadius: '8px',
                                            border: '1px solid rgba(239, 68, 68, 0.3)',
                                            background: 'rgba(239, 68, 68, 0.1)',
                                            color: '#f87171',
                                            fontSize: '0.9rem',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '8px',
                                            transition: 'all 0.2s'
                                        }}
                                    >
                                        <Icon icon="solar:close-circle-bold" />
                                        {isTrialActive ? 'Cancel Free Trial' : 'Cancel Subscription'}
                                    </button>

                                    <p style={{ 
                                        margin: '10px 0 0 0', 
                                        color: '#64748b', 
                                        fontSize: '0.75rem',
                                        textAlign: 'center'
                                    }}>
                                        {isTrialActive 
                                            ? "Cancel anytime. You won't be charged."
                                            : "You'll retain access until the end of your billing period."
                                        }
                                    </p>
                                </div>


                            </div>
                        )}

                        {/* iOS Billing Info */}
                        {isIOS && (isPaidPremium || isTrialActive) && (
                            <div className="profile__card" style={{ marginTop: '16px' }}>
                                <div className="profile__card-head">
                                    <div className="profile__card-icon" style={{ background: 'rgba(148, 163, 184, 0.15)', color: '#94a3b8' }}>
                                        <Icon icon="simple-icons:apple" />
                                    </div>
                                    <div>
                                        <h3 className="profile__card-title">Billing</h3>
                                        <p className="profile__card-subtitle">Managed by Apple</p>
                                    </div>
                                </div>

                                <p style={{ 
                                    color: '#94a3b8', 
                                    fontSize: '0.85rem', 
                                    marginTop: '12px',
                                    lineHeight: '1.5'
                                }}>
                                    Your subscription is managed through the App Store. To cancel or modify your plan:
                                </p>

                                <div style={{
                                    marginTop: '12px',
                                    padding: '12px',
                                    background: 'rgba(30, 41, 59, 0.5)',
                                    borderRadius: '8px',
                                    fontSize: '0.85rem',
                                    color: '#e2e8f0'
                                }}>
                                    <p style={{ margin: '0 0 8px 0' }}>
                                        <strong>Settings</strong> → <strong>Apple ID</strong> → <strong>Subscriptions</strong> → <strong>AiPredict</strong>
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* --- RIGHT COL: ACCOUNT DATA --- */}
                    <div className="profile__col-right">
                        
                        {/* Personal Data */}
                        <div className="profile__card">
                            <h3 className="profile__section-title">Personal Data</h3>
                            
                            <div className="profile__data-row">
                                <div className="profile__data-label">Display Name</div>
                                <div className="profile__data-value">{user?.displayName || profile?.displayName || 'Trader'}</div>
                            </div>

                            <div className="profile__data-row">
                                <div className="profile__data-label">Email Address</div>
                                <div className="profile__data-value">{user?.email}</div>
                            </div>

                            <div className="profile__data-row">
                                <div className="profile__data-label">User ID (UID)</div>
                                <div className="profile__data-value profile__uid-container">
                                    <span className="profile__uid-dots">•••• •••• •••• </span>
                                    <span className="profile__uid-last">{user?.uid?.slice(-4)}</span>
                                    <button 
                                        className="profile__copy-btn"
                                        onClick={() => copyToClipboard(user?.uid)}
                                        title="Copy full UID"
                                    >
                                        <Icon icon="solar:copy-linear" />
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Security & Support */}
                        <div className="profile__card">
                            <h3 className="profile__section-title">System Controls</h3>
                            
                            <div className="profile__security-actions">
                                
                                {/* Support Button */}
                                <button 
                                    className="profile__action-btn" 
                                    onClick={() => navigate('/support')}
                                >
                                    <div className="profile__action-icon">
                                        <Icon icon="solar:headphones-round-sound-bold" />
                                    </div>
                                    <div className="profile__action-text">
                                        <span>Help & Support</span>
                                        <small>Chat with our team</small>
                                    </div>
                                    <Icon icon="solar:alt-arrow-right-linear" className="profile__arrow" />
                                </button>

                                {/* Reset Password */}
                                <button 
                                    className="profile__action-btn" 
                                    onClick={handleResetPassword}
                                    disabled={loading}
                                >
                                    <div className="profile__action-icon">
                                        <Icon icon="solar:lock-password-linear" />
                                    </div>
                                    <div className="profile__action-text">
                                        <span>Reset Password</span>
                                        <small>Send a recovery link to email</small>
                                    </div>
                                    <Icon icon="solar:alt-arrow-right-linear" className="profile__arrow" />
                                </button>

                                {/* Logout */}
                                <button 
                                    className="profile__action-btn profile__action-btn--danger" 
                                    onClick={handleLogout}
                                >
                                    <div className="profile__action-icon">
                                        <Icon icon="solar:logout-2-linear" />
                                    </div>
                                    <div className="profile__action-text">
                                        <span>Terminate Session</span>
                                        <small>Sign out of this device</small>
                                    </div>
                                    <Icon icon="solar:alt-arrow-right-linear" className="profile__arrow" />
                                </button>
                            </div>
                        </div>

                        {/* DANGER ZONE */}
                        <div className="profile__card profile__card--danger">
                            <h3 className="profile__section-title" style={{ color: '#ef4444' }}>
                                <Icon icon="solar:danger-triangle-bold" style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                                Danger Zone
                            </h3>
                            
                            <p style={{ color: '#94a3b8', fontSize: '0.85rem', marginBottom: '1rem' }}>
                                Permanently delete your account and all associated data. This action cannot be undone.
                            </p>

                            <button 
                                className="profile__action-btn profile__action-btn--delete" 
                                onClick={handleOpenDeleteModal}
                                style={{
                                    background: 'rgba(239, 68, 68, 0.1)',
                                    border: '1px solid rgba(239, 68, 68, 0.3)',
                                }}
                            >
                                <div className="profile__action-icon" style={{ background: 'rgba(239, 68, 68, 0.2)', color: '#ef4444' }}>
                                    <Icon icon="solar:trash-bin-trash-bold" />
                                </div>
                                <div className="profile__action-text">
                                    <span style={{ color: '#ef4444' }}>Delete Account</span>
                                    <small>Permanently remove all your data</small>
                                </div>
                                <Icon icon="solar:alt-arrow-right-linear" className="profile__arrow" style={{ color: '#ef4444' }} />
                            </button>
                        </div>

                    </div>
                </div>
            </div>

            {/* =========================================================
                🔥 CANCEL SUBSCRIPTION MODAL
            ========================================================= */}
            {showCancelModal && (
                <div className="profile__modal-overlay">
                    <div className="profile__modal">
                        <button 
                            className="profile__modal-close" 
                            onClick={() => setShowCancelModal(false)}
                            disabled={cancelling}
                        >
                            <Icon icon="solar:close-circle-linear" />
                        </button>

                        <div className="profile__modal-icon profile__modal-icon--warning">
                            <Icon icon="solar:sad-circle-bold" />
                        </div>
                        
                        <h2 className="profile__modal-title">
                            {isTrialActive ? 'Cancel Free Trial?' : 'Cancel Subscription?'}
                        </h2>
                        
                        <p className="profile__modal-text">
                            {isTrialActive 
                                ? "Are you sure you want to cancel your free trial? You'll lose access to premium features immediately."
                                : "Are you sure you want to cancel? You'll retain access until the end of your current billing period."
                            }
                        </p>

                        <div style={{
                            background: 'rgba(251, 191, 36, 0.1)',
                            border: '1px solid rgba(251, 191, 36, 0.3)',
                            borderRadius: '8px',
                            padding: '12px',
                            margin: '16px 0',
                            textAlign: 'left'
                        }}>
                            <p style={{ color: '#fbbf24', fontSize: '0.85rem', margin: 0, fontWeight: '500' }}>
                                What you'll lose:
                            </p>
                            <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px', color: '#94a3b8', fontSize: '0.85rem' }}>
                                <li>5 AI searches per day</li>
                                <li>Premium Football Singles</li>
                                <li>HR/HR Volatility Picks</li>
                                <li>Full Crypto AI Access</li>
                            </ul>
                        </div>

                        {cancelError && (
                            <p className="profile__modal-error">
                                <Icon icon="solar:danger-triangle-linear" /> {cancelError}
                            </p>
                        )}

                        <div className="profile__modal-actions">
                            <button 
                                className="profile__btn profile__btn--primary"
                                onClick={() => setShowCancelModal(false)}
                                disabled={cancelling}
                            >
                                Keep {isTrialActive ? 'Trial' : 'Subscription'}
                            </button>
                            <button 
                                className="profile__btn profile__btn--ghost"
                                onClick={handleCancelTrial}
                                disabled={cancelling}
                                style={{ color: '#f87171', borderColor: 'rgba(239, 68, 68, 0.3)' }}
                            >
                                {cancelling ? 'Cancelling...' : 'Yes, Cancel'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* =========================================================
                DELETE ACCOUNT MODAL
            ========================================================= */}
            {showDeleteModal && (
                <div className="profile__modal-overlay">
                    <div className="profile__modal">
                        <button 
                            className="profile__modal-close" 
                            onClick={handleCloseDeleteModal}
                            disabled={deleting}
                        >
                            <Icon icon="solar:close-circle-linear" />
                        </button>

                        {/* Step 1: Confirmation */}
                        {deleteStep === 1 && (
                            <>
                                <div className="profile__modal-icon profile__modal-icon--danger">
                                    <Icon icon="solar:trash-bin-trash-bold" />
                                </div>
                                <h2 className="profile__modal-title">Delete Account?</h2>
                                <p className="profile__modal-text">
                                    This will <strong>permanently delete</strong> your account, including:
                                </p>
                                <ul className="profile__modal-list">
                                    <li><Icon icon="solar:close-circle-linear" /> Your profile and settings</li>
                                    <li><Icon icon="solar:close-circle-linear" /> Prediction history</li>
                                    <li><Icon icon="solar:close-circle-linear" /> All saved preferences</li>
                                    {isPaidPremium && (
                                        <li><Icon icon="solar:close-circle-linear" /> Premium subscription access</li>
                                    )}
                                </ul>
                                <p className="profile__modal-warning">
                                    ⚠️ This action <strong>cannot be undone</strong>.
                                </p>

                                <div className="profile__modal-actions">
                                    <button 
                                        className="profile__btn profile__btn--ghost"
                                        onClick={handleCloseDeleteModal}
                                    >
                                        Cancel
                                    </button>
                                    <button 
                                        className="profile__btn profile__btn--danger"
                                        onClick={handleConfirmDelete}
                                    >
                                        Continue
                                    </button>
                                </div>
                            </>
                        )}

                        {/* Step 2: Re-authentication */}
                        {deleteStep === 2 && (
                            <>
                                <div className="profile__modal-icon profile__modal-icon--warning">
                                    <Icon icon="solar:lock-password-bold" />
                                </div>
                                <h2 className="profile__modal-title">Confirm Your Identity</h2>
                                <p className="profile__modal-text">
                                    For security, please enter your password to delete your account.
                                </p>

                                <div className="profile__modal-input-group">
                                    <label>Password</label>
                                    <input 
                                        type="password" 
                                        value={deletePassword}
                                        onChange={(e) => setDeletePassword(e.target.value)}
                                        placeholder="Enter your password"
                                        className="profile__modal-input"
                                        autoFocus
                                    />
                                </div>

                                {deleteError && (
                                    <p className="profile__modal-error">
                                        <Icon icon="solar:danger-triangle-linear" /> {deleteError}
                                    </p>
                                )}

                                <div className="profile__modal-actions">
                                    <button 
                                        className="profile__btn profile__btn--ghost"
                                        onClick={handleCloseDeleteModal}
                                        disabled={deleting}
                                    >
                                        Cancel
                                    </button>
                                    <button 
                                        className="profile__btn profile__btn--danger"
                                        onClick={handleDeleteAccount}
                                        disabled={!deletePassword || deleting}
                                    >
                                        {deleting ? 'Deleting...' : 'Delete My Account'}
                                    </button>
                                </div>
                            </>
                        )}

                        {/* Step 3: Deleting */}
                        {deleteStep === 3 && (
                            <>
                                <div className="profile__modal-icon profile__modal-icon--loading">
                                    <Icon icon="solar:refresh-bold" className="profile__spinner" />
                                </div>
                                <h2 className="profile__modal-title">Deleting Account...</h2>
                                <p className="profile__modal-text">
                                    Please wait while we remove your data. This may take a moment.
                                </p>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Modal Styles */}
            <style>{`
                .profile__modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(0, 0, 0, 0.8);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 9999;
                    padding: 20px;
                }

                .profile__modal {
                    background: #1e293b;
                    border-radius: 16px;
                    padding: 2rem;
                    max-width: 420px;
                    width: 100%;
                    position: relative;
                    border: 1px solid rgba(71, 85, 105, 0.5);
                    text-align: center;
                }

                .profile__modal-close {
                    position: absolute;
                    top: 1rem;
                    right: 1rem;
                    background: none;
                    border: none;
                    color: #64748b;
                    font-size: 1.5rem;
                    cursor: pointer;
                    transition: color 0.2s;
                }

                .profile__modal-close:hover {
                    color: #fff;
                }

                .profile__modal-close:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }

                .profile__modal-icon {
                    width: 64px;
                    height: 64px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin: 0 auto 1.5rem;
                    font-size: 2rem;
                }

                .profile__modal-icon--danger {
                    background: rgba(239, 68, 68, 0.2);
                    color: #ef4444;
                }

                .profile__modal-icon--warning {
                    background: rgba(251, 191, 36, 0.2);
                    color: #fbbf24;
                }

                .profile__modal-icon--loading {
                    background: rgba(59, 130, 246, 0.2);
                    color: #3b82f6;
                }

                .profile__modal-title {
                    color: #fff;
                    font-size: 1.25rem;
                    margin-bottom: 0.75rem;
                }

                .profile__modal-text {
                    color: #94a3b8;
                    font-size: 0.9rem;
                    margin-bottom: 1rem;
                    line-height: 1.5;
                }

                .profile__modal-list {
                    list-style: none;
                    padding: 0;
                    margin: 1rem 0;
                    text-align: left;
                }

                .profile__modal-list li {
                    color: #94a3b8;
                    font-size: 0.85rem;
                    padding: 0.5rem 0;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }

                .profile__modal-list li svg {
                    color: #ef4444;
                    flex-shrink: 0;
                }

                .profile__modal-warning {
                    background: rgba(239, 68, 68, 0.1);
                    border: 1px solid rgba(239, 68, 68, 0.3);
                    border-radius: 8px;
                    padding: 0.75rem;
                    color: #fca5a5;
                    font-size: 0.85rem;
                    margin: 1rem 0;
                }

                .profile__modal-input-group {
                    text-align: left;
                    margin-bottom: 1rem;
                }

                .profile__modal-input-group label {
                    display: block;
                    color: #94a3b8;
                    font-size: 0.85rem;
                    margin-bottom: 0.5rem;
                }

                .profile__modal-input {
                    width: 100%;
                    padding: 12px 16px;
                    background: #0f172a;
                    border: 1px solid #334155;
                    border-radius: 8px;
                    color: #fff;
                    font-size: 1rem;
                    outline: none;
                    transition: border-color 0.2s;
                }

                .profile__modal-input:focus {
                    border-color: #3b82f6;
                }

                .profile__modal-error {
                    color: #ef4444;
                    font-size: 0.85rem;
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    justify-content: center;
                    margin-bottom: 1rem;
                }

                .profile__modal-actions {
                    display: flex;
                    gap: 12px;
                    margin-top: 1.5rem;
                }

                .profile__modal-actions .profile__btn {
                    flex: 1;
                }

                .profile__btn--danger {
                    background: #ef4444 !important;
                    border-color: #ef4444 !important;
                    color: #fff !important;
                }

                .profile__btn--danger:hover {
                    background: #dc2626 !important;
                }

                .profile__btn--danger:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }

                .profile__card--danger {
                    border: 1px solid rgba(239, 68, 68, 0.2);
                    background: rgba(239, 68, 68, 0.05);
                }

                .profile__spinner {
                    animation: spin 1s linear infinite;
                }

                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </section>
    );
};

export default Profile;